import { isBefore, parseISO, startOfDay } from "date-fns";
import type { InspectionDisplayStatus, InspectionStatus } from "@/types";

export type InspectionCardPrimaryAction = "start" | "continue" | "view";

interface InspectionActionInspection {
  status: InspectionStatus;
  due_date: string | null;
  assigned_to: string | null;
  created_by?: string | null;
  isAssigneeUnavailable?: boolean;
}

interface InspectionCardActionCallbacks {
  start?: boolean;
  continue?: boolean;
  view?: boolean;
  edit?: boolean;
  reopen?: boolean;
  cancel?: boolean;
  delete?: boolean;
  assign?: boolean;
}

interface BuildInspectionCardActionModelInput {
  inspection: InspectionActionInspection;
  currentUserId?: string;
  isQualitaetsmanager?: boolean;
  callbacks?: InspectionCardActionCallbacks;
  assignableUserCount?: number;
  showMenu?: boolean;
  today?: Date;
}

export interface InspectionCardActionModel {
  displayStatus: InspectionDisplayStatus;
  isOverdue: boolean;
  primaryAction: InspectionCardPrimaryAction | null;
  assignment: {
    canAssignUnassigned: boolean;
    canReassignUnavailableAssignee: boolean;
  };
  menu: {
    hasMenuItems: boolean;
    showDetails: boolean;
    edit: boolean;
    reopen: boolean;
    cancel: boolean;
    delete: boolean;
  };
}

const primaryActionByStatus: Record<InspectionStatus, InspectionCardPrimaryAction | null> = {
  pending: "start",
  in_progress: "continue",
  completed: "view",
  cancelled: null,
};

const actionsRequiringAssignee = new Set<InspectionCardPrimaryAction>(["start", "continue"]);
const noAssignmentActions: InspectionCardActionModel["assignment"] = {
  canAssignUnassigned: false,
  canReassignUnavailableAssignee: false,
};

const isTerminalInspectionStatus = (status: InspectionStatus): boolean =>
  status === "completed" || status === "cancelled";

const isOpenActionAvailable = (requested: boolean | undefined, isTerminal: boolean): boolean =>
  !isTerminal && Boolean(requested);

const hasRequestedPrimaryAction = (
  action: InspectionCardPrimaryAction | null,
  callbacks: InspectionCardActionCallbacks,
): boolean => Boolean(action && callbacks[action]);

const isPrimaryActionAllowedForUser = (
  action: InspectionCardPrimaryAction | null,
  inspection: InspectionActionInspection,
  currentUserId: string | undefined,
): boolean => {
  if (!action) {
    return false;
  }

  if (!actionsRequiringAssignee.has(action)) {
    return true;
  }

  return inspection.assigned_to === currentUserId;
};

const isInspectionOverdue = (
  inspection: InspectionActionInspection,
  today: Date = new Date(),
): boolean => {
  if (isTerminalInspectionStatus(inspection.status)) {
    return false;
  }

  if (!inspection.due_date) {
    return false;
  }

  const dueDate = parseISO(inspection.due_date);
  return isBefore(dueDate, startOfDay(today));
};

const getDisplayStatus = (
  inspection: InspectionActionInspection,
  today: Date = new Date(),
): InspectionDisplayStatus => {
  if (isTerminalInspectionStatus(inspection.status)) {
    return inspection.status;
  }

  if (isInspectionOverdue(inspection, today)) {
    return "overdue";
  }

  return inspection.status;
};

const getPrimaryAction = (
  inspection: InspectionActionInspection,
  currentUserId: string | undefined,
  callbacks: InspectionCardActionCallbacks,
): InspectionCardPrimaryAction | null => {
  const action = primaryActionByStatus[inspection.status];

  return hasRequestedPrimaryAction(action, callbacks) &&
    isPrimaryActionAllowedForUser(action, inspection, currentUserId)
    ? action
    : null;
};

const canReopenInspection = (
  inspection: InspectionActionInspection,
  displayStatus: InspectionDisplayStatus,
  currentUserId: string | undefined,
  isQualitaetsmanager: boolean,
  callbacks: InspectionCardActionCallbacks,
): boolean =>
  Boolean(
    displayStatus === "completed" &&
    callbacks.reopen &&
    (isQualitaetsmanager || inspection.created_by === currentUserId),
  );

const getAssignmentActions = (
  inspection: InspectionActionInspection,
  callbacks: InspectionCardActionCallbacks,
  assignableUserCount: number,
  isTerminal: boolean,
): InspectionCardActionModel["assignment"] => {
  const canAssign = isOpenActionAvailable(callbacks.assign, isTerminal) && assignableUserCount > 0;
  if (!canAssign) {
    return noAssignmentActions;
  }

  const isAssigned = Boolean(inspection.assigned_to);

  return {
    canAssignUnassigned: !isAssigned,
    canReassignUnavailableAssignee: Boolean(inspection.isAssigneeUnavailable) && isAssigned,
  };
};

const getMenuActions = (
  callbacks: InspectionCardActionCallbacks,
  showMenu: boolean,
  isTerminal: boolean,
  canReopen: boolean,
  primaryAction: InspectionCardPrimaryAction | null,
): InspectionCardActionModel["menu"] => {
  const canCancel = isOpenActionAvailable(callbacks.cancel, isTerminal);
  const canDelete = isOpenActionAvailable(callbacks.delete, isTerminal);
  // TES-1709: suppress the redundant "Details anzeigen" menu entry when the primary
  // "Ansehen" button already exposes the view action (completed inspections). Pending/
  // in-progress/cancelled cards have no primary view button, so the menu entry stays as
  // their view path alongside the whole-card click. Gating hasVisibleMenuRequest on the
  // same value prevents an empty overflow menu when view was the only would-be entry.
  const showDetails = Boolean(callbacks.view) && primaryAction !== "view";
  const hasVisibleMenuRequest = [callbacks.edit, showDetails, canReopen, canCancel, canDelete].some(
    Boolean,
  );

  return {
    hasMenuItems: showMenu && hasVisibleMenuRequest,
    showDetails,
    edit: isOpenActionAvailable(callbacks.edit, isTerminal),
    reopen: canReopen,
    cancel: canCancel,
    delete: canDelete,
  };
};

export const buildInspectionCardActionModel = ({
  inspection,
  currentUserId,
  isQualitaetsmanager = false,
  callbacks = {},
  assignableUserCount = 0,
  showMenu = true,
  today = new Date(),
}: BuildInspectionCardActionModelInput): InspectionCardActionModel => {
  const displayStatus = getDisplayStatus(inspection, today);
  const isOverdue = displayStatus === "overdue";
  const isTerminal = isTerminalInspectionStatus(inspection.status);
  const canReopen = canReopenInspection(
    inspection,
    displayStatus,
    currentUserId,
    isQualitaetsmanager,
    callbacks,
  );

  const primaryAction = getPrimaryAction(inspection, currentUserId, callbacks);

  return {
    displayStatus,
    isOverdue,
    primaryAction,
    assignment: getAssignmentActions(inspection, callbacks, assignableUserCount, isTerminal),
    menu: getMenuActions(callbacks, showMenu, isTerminal, canReopen, primaryAction),
  };
};
